import React, { useState, useEffect } from "react";
import { FormInput } from "components/ui/FormInput";
import { ButtonLoader } from "components/Loader/ButtonLoader";
import { Plus, X } from "lucide-react";
import { IconSelector } from "components/ui/IconSelector";

import "styles/main.css";

export const PartnerServiceForm = ({
  onSubmit,
  register,
  errors,
  isLoading,
  defaultValues = {},
  setValue,
  watch,
  control
}) => {
  // Parse items from defaultValues (could be raw database data or processed data)
  const getInitialItems = () => {
    if (defaultValues?.items) {
      // Items are stored as "@" separated string in database
      if (typeof defaultValues.items === 'string') {
        return defaultValues.items.split('@').filter(item => item.trim());
      }
      if (Array.isArray(defaultValues.items)) {
        return defaultValues.items;
      }
    }
    return [""];
  };

  const getInitialDescription = () => {
    if (defaultValues?.description) {
      return defaultValues.description;
    }
    return '';
  };

  const [listItems, setListItems] = useState(getInitialItems());
  const [selectedIcon, setSelectedIcon] = useState(defaultValues?.icon || "");
  const [useDescription, setUseDescription] = useState(
    (defaultValues && Object.keys(defaultValues).length > 0) 
      ? (defaultValues?.content_type === 'description' || !!defaultValues?.description)
      : false  // Default to List Items for new forms
  );

  // Avoid watching all fields to prevent unnecessary re-renders
  // const watchedFields = watch();

  useEffect(() => {
    const hasDefaults = defaultValues && Object.keys(defaultValues).length > 0;
    if (!hasDefaults) return; // don't reset for empty defaults on Add form

    const newItems = getInitialItems();
    const newUseDescription = (defaultValues?.content_type === 'description' || !!defaultValues?.description);

    setListItems(newItems);
    setSelectedIcon(defaultValues?.icon || "");
    setUseDescription(newUseDescription);

    if (newItems.length > 0) {
      setValue("items", newItems, { shouldValidate: false });
    }
  }, [defaultValues, setValue]);

  // Handle dynamic list items
  const addListItem = () => {
    const newItems = [...listItems, ""];
    setListItems(newItems);
    setValue("items", newItems, { shouldValidate: true });
  };

  const removeListItem = (index) => {
    if (listItems.length > 1) {
      const newItems = listItems.filter((_, i) => i !== index);
      setListItems(newItems);
      setValue("items", newItems, { shouldValidate: true });
    }
  };

  const updateListItem = (index, value) => {
    const newItems = [...listItems];
    newItems[index] = value;
    setListItems(newItems);
    setValue("items", newItems, { shouldValidate: true });
  };

  // Handle icon selection
  const handleIconSelect = (iconKey) => {
    setSelectedIcon(iconKey);
    setValue("icon", iconKey, { shouldValidate: true });
  };

  // Handle content type toggle
  const toggleContentType = (type) => {
    const isDescriptionMode = type === 'description';
    setUseDescription(isDescriptionMode);
    setValue('contentType', isDescriptionMode ? 'description' : 'items', { shouldValidate: true });
  };

  return (
    <div className="partner_service_form">
      <form onSubmit={onSubmit} className="row mt-4 mb-3">
        
        {/* Title */}
        <div className="mb-3 col-md-6">
          <label htmlFor="title" className="form-label">
            Service Title <span className="text-danger">*</span>
          </label>
          <FormInput
            type="text"
            name="title"
            placeholder="Enter service title"
            {...register("title", { required: "Title is required" })}
          />
          {errors?.title && (
            <p className="errorMessage">{errors.title.message}</p>
          )}
        </div>

        {/* Display Order */}
        <div className="mb-3 col-md-6">
          <label htmlFor="displayOrder" className="form-label">
            Display Order <span className="text-danger">*</span>
          </label>
          <FormInput
            type="number"
            name="displayOrder"
            placeholder="Enter display order (1, 2, 3...)"
            min="1"
            {...register("displayOrder", { 
              required: "Display order is required",
              min: { value: 1, message: "Display order must be at least 1" }
            })}
          />
          {errors?.displayOrder && (
            <p className="errorMessage">{errors.displayOrder.message}</p>
          )}
        </div>

        {/* Icon Selector (inline popover grid) */}
        <div className="mb-3 col-md-6" style={{ position: 'relative', zIndex: 10000 }}>
          <IconSelector
            selectedIcon={selectedIcon}
            onIconSelect={(key) => {
              setSelectedIcon(key);
              setValue("icon", key, { shouldValidate: true });
            }}
            error={!!errors?.icon}
          />
          <input type="hidden" {...register("icon", { required: "Icon is required" })} value={selectedIcon} />
          {errors?.icon && <p className="errorMessage">{errors.icon.message}</p>}
        </div>

        {/* Status */}
        <div className="mb-3 col-md-6">
          <label htmlFor="status" className="form-label">
            Status
          </label>
          <select
            className="form-select"
            {...register("status")}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Link URL */}
        <div className="mb-3 col-md-6">
          <label htmlFor="linkUrl" className="form-label">
            Link URL <span className="text-danger">*</span>
          </label>
          <FormInput
            type="text"
            name="linkUrl"
            placeholder="Enter link URL"
            {...register("linkUrl", { required: "Link URL is required" })}
          />
          {errors?.linkUrl && (
            <p className="errorMessage">{errors.linkUrl.message}</p>
          )}
        </div>

        {/* Content Type Toggle (simple buttons) */}
        <div className="mb-3 col-12" style={{ position: 'relative', zIndex: 10000 }}>
          <label className="form-label d-block">Content Type</label>
          <div className="btn-group" role="group" aria-label="Content Type Toggle">
            <button
              type="button"
              className={`btn btn-sm ${!useDescription ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => toggleContentType('list')}
              style={{ pointerEvents: 'auto' }}
            >
              List Items
            </button>
            <button
              type="button"
              className={`btn btn-sm ${useDescription ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => toggleContentType('description')}
              style={{ pointerEvents: 'auto' }}
            >
              Description
            </button>
          </div>
        </div>

        {/* Dynamic List Items */}
        {!useDescription && (
          <div className="mb-3 col-12" style={{ position: 'relative', zIndex: 10000 }}>
            <label className="form-label">
              Service Features/Items <span className="text-danger">*</span>
            </label>
            {(Array.isArray(listItems) ? listItems : [""]).map((item, index) => (
              <div key={index} className="d-flex align-items-center gap-2 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Feature ${index + 1}`}
                  value={item}
                  onChange={(e) => updateListItem(index, e.target.value)}
                  style={{ flex: 1, pointerEvents: 'auto' }}
                />
                {listItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeListItem(index)}
                    className="btn btn-danger btn-sm"
                    style={{ minWidth: '40px' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addListItem}
              className="btn btn-primary btn-sm d-flex align-items-center gap-2"
            >
              <Plus size={16} />
              Add More Item
            </button>
            {!listItems.some(item => item.trim()) && (
              <p className="errorMessage">At least one item is required</p>
            )}
            {/* Hidden input to register items with form */}
            <input
              type="hidden"
              {...register("items", { 
                required: !useDescription ? "At least one item is required" : false 
              })}
              value={JSON.stringify(listItems)}
            />
          </div>
        )}

        {/* Description */}
        {useDescription && (
          <div className="mb-3 col-12">
            <label htmlFor="description" className="form-label">
              Service Description <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              name="description"
              rows="4"
              placeholder="Enter service description"
              {...register("description", { 
                required: useDescription ? "Description is required" : false 
              })}
            />
            {errors?.description && (
              <p className="errorMessage">{errors.description.message}</p>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="col-12 text-center">
            <ButtonLoader />
          </div>
        ) : (
          <div className="mb-3 col-12 text-center">
            <input 
              type="submit" 
              value="Save Service" 
              className="btn btn-primary primary-bg-color outline-0 border-0 py-2"
              style={{
                backgroundColor: "#2a3071",
                width: "200px"
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#34caff")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#2a3071")}
            />
          </div>
        )}
      </form>
    </div>
  );
};